import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Task, Priority } from '../mocks/crm';

function rowToTask(r: Record<string, unknown>): Task {
  const dbStatus = (r.status as string) || 'pending';
  const status: Task['status'] = dbStatus === 'completed' ? 'done' : (dbStatus as Task['status']);
  return {
    id: r.id as string,
    title: (r.title as string) || '',
    description: (r.description as string) || '',
    priority: ((r.priority as string) || 'medium') as Priority,
    status,
    dueDate: (r.due_date as string) || '',
    dueTime: '09:00',
    assignedTo: 'You',
    assignedAvatar: 'YO',
    linkedLead: (r.lead_id as string) || '',
    linkedLeadName: '',
    category: 'other' as Task['category'],
    createdAt: (r.created_at as string) || '',
  };
}

export function useTasks() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('tasks').select('*').order('created_at', { ascending: false });
    setTasks((data || []).map((r) => rowToTask(r as Record<string, unknown>)));
    setLoading(false);
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const createTask = async (task: Partial<Task>) => {
    const { data, error } = await supabase.from('tasks').insert({
      title: task.title,
      description: task.description,
      priority: task.priority || 'medium',
      status: task.status === 'done' ? 'completed' : task.status || 'pending',
      due_date: task.dueDate || null,
      lead_id: task.linkedLead || null,
    }).select().maybeSingle();
    if (!error && data) setTasks((prev) => [rowToTask(data as Record<string, unknown>), ...prev]);
    return { data, error };
  };

  const toggleTask = async (id: string) => {
    const task = tasks.find((t) => t.id === id);
    if (!task) return;
    const newDbStatus = task.status === 'done' ? 'pending' : 'completed';
    const newUiStatus: Task['status'] = task.status === 'done' ? 'pending' : 'done';
    const { error } = await supabase.from('tasks').update({ status: newDbStatus }).eq('id', id);
    if (!error) setTasks((prev) => prev.map((t) => t.id === id ? { ...t, status: newUiStatus } : t));
  };

  const deleteTask = async (id: string) => {
    const { error } = await supabase.from('tasks').delete().eq('id', id);
    if (!error) setTasks((prev) => prev.filter((t) => t.id !== id));
  };

  return { tasks, loading, refetch: fetchTasks, createTask, toggleTask, deleteTask };
}
